import { useEffect, useMemo, useState } from "react";
import NewTitle from "../../extra/Title";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { connect, useDispatch, useSelector } from "react-redux";
import { getProfile } from "../../store/admin/admin.action";
import {
  getDashboardCount,
  getDashboardUserChart,
  getChartAnalyticOfActiveUser,
  getAdminEarningChart,
  getRecentMonetizationRequests,
  getRecentWithdrawRequests,
} from "../../store/dashboard/dashboard.action";
import {
  IconBrandYoutube,
  IconMovie,
  IconUser,
  IconVideo,
  IconCash,
  IconCashBanknote,
  IconReport,
  IconWallet,
  IconCoin,
  IconPremiumRights,
  IconAd,
  IconUsers,
  IconChartAreaLine,
  IconCalendarEvent,
  IconChevronDown,
  IconInfoCircle,
  IconTrendingUp,
  IconTrendingDown,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { Skeleton } from "@mui/material";
import Table from "../../extra/Table";
import LazyImage from "../../../common/ImageFallback";
import UniqueIdCopy from "../../extra/UniqueIdCopy";
import { attachDateRangeCustomActiveFix } from "../../extra/dateRangePickerFix";
import { StatsCardSkeleton } from "../../extra/StatsCards";

const AnalyticsMetricSkeleton = () => (
  <div className="da-metric-card">
    <Skeleton
      variant="rounded"
      width={48}
      height={48}
      style={{ borderRadius: 12, flexShrink: 0 }}
    />
    <div className="da-metric-card__content" style={{ flex: 1 }}>
      <Skeleton
        variant="rounded"
        width="55%"
        height={14}
        style={{ borderRadius: 4, marginBottom: 10 }}
      />
      <Skeleton
        variant="rounded"
        width="40%"
        height={28}
        style={{ borderRadius: 6 }}
      />
    </div>
  </div>
);

const ANALYTICS_PERIODS = [
  { key: "7D", label: "7D", days: 6 },
  { key: "30D", label: "30D", days: 29 },
  { key: "3M", label: "3M", months: 3 },
  { key: "6M", label: "6M", months: 6 },
  { key: "1Y", label: "1Y", years: 1 },
];

const datePickerLocale = {
  format: "DD/MM/YYYY",
  separator: " - ",
  applyLabel: "Apply",
  cancelLabel: "Cancel",
  customRangeLabel: "Custom Range",
  daysOfWeek: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  monthNames: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],
  firstDay: 0,
};

const getDateRanges = () => ({
  "All Time": [new Date("1970-01-01"), dayjs().toDate()],
  Today: [dayjs().toDate(), dayjs().toDate()],
  Yesterday: [
    dayjs().subtract(1, "days").toDate(),
    dayjs().subtract(1, "days").toDate(),
  ],
  "Last 7 Days": [dayjs().subtract(6, "days").toDate(), dayjs().toDate()],
  "Last 30 Days": [dayjs().subtract(29, "days").toDate(), dayjs().toDate()],
  "This Month": [
    dayjs().startOf("month").toDate(),
    dayjs().endOf("month").toDate(),
  ],
  "Last Month": [
    dayjs().subtract(1, "month").startOf("month").toDate(),
    dayjs().subtract(1, "month").endOf("month").toDate(),
  ],
});

const calcSeriesTrend = (series = []) => {
  if (!series || series.length < 2) return null;
  const mid = Math.floor(series.length / 2);
  const firstHalf = series.slice(0, mid);
  const secondHalf = series.slice(mid);
  const avg = (arr) =>
    arr.length ? arr.reduce((sum, n) => sum + (Number(n) || 0), 0) / arr.length : 0;
  const first = avg(firstHalf);
  const second = avg(secondHalf);
  if (first === 0 && second === 0) return null;
  if (first === 0) return { pct: 100, up: second > 0 };
  const change = Math.round(((second - first) / first) * 100);
  return { pct: Math.abs(change), up: change >= 0 };
};

const formatAxisDate = (value) => {
  if (!value || !dayjs(value).isValid()) return value || "";
  return dayjs(value).format("MMM DD");
};

const ChartEmptyState = ({ title, height = 300, message }) => (
  <div
    className="d-flex flex-column align-items-center justify-content-center text-center px-3"
    style={{ height, minHeight: Math.min(height, 220) }}
  >
    <p className="mb-3 text-muted" style={{ fontSize: 15 }}>
      {title}
    </p>
    <div
      className="d-flex align-items-center justify-content-center mb-3"
      style={{
        width: 56,
        height: 56,
        borderRadius: "50%",
        background: "rgba(255, 94, 117, 0.12)",
      }}
    >
      <IconChartAreaLine size={26} color="#ff5e75" stroke={1.6} />
    </div>
    <h6 className="fw-semibold mb-1" style={{ color: "#1f2937" }}>
      No {title} Found
    </h6>
    <p className="mb-0 text-muted" style={{ fontSize: 13, maxWidth: 280 }}>
      {message ||
        "Data will appear here once available. Try changing the date range."}
    </p>
  </div>
);

const AnalyticsMetricCard = ({ title, count, Icon, accent }) => {
  return (
    <div className={`da-metric-card ${accent || ""}`}>
      <div className="da-metric-card__arcs" aria-hidden="true">
        <span className="da-metric-card__arc da-metric-card__arc--outer" />
        <span className="da-metric-card__arc da-metric-card__arc--inner" />
      </div>
      <div className={`da-metric-card__icon ${accent || ""}`}>
        <Icon size={22} stroke={1.8} />
      </div>
      <div className="da-metric-card__content">
        <span className="da-metric-card__title">{title}</span>
        <h3 className="da-metric-card__value">{count ?? 0}</h3>
      </div>
    </div>
  );
};

const Dashboard = (props) => {
  const [startDate, setStartDate] = useState("All");
  const [endDate, setEndDate] = useState("All");
  const [analyticsPeriod, setAnalyticsPeriod] = useState(null);
  const {
    dashboardCount,
    chartAnalyticOfVideos,
    chartAnalyticOfShorts,
    chartAnalyticOfUsers,
    chartAnalyticOfActiveUser,
    adminEarningChart,
    recentMonetizationRequests,
    recentWithdrawRequests,
  } = useSelector((state) => state.dashboard);
  const { isLoading } = useSelector((state) => state.dialogue);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const startDateFormat = (value) => {
    if (value === "All") return "All";
    return dayjs(value).isValid()
      ? dayjs(value).format("YYYY-MM-DD")
      : dayjs().subtract(7, "day").format("YYYY-MM-DD");
  };

  const endDateFormat = (value) => {
    if (value === "All") return "All";
    return dayjs(value).isValid()
      ? dayjs(value).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
  };
  const startDateData = startDateFormat(startDate);
  const endDateData = endDateFormat(endDate);

  useEffect(() => {
    dispatch(getDashboardCount(startDateData, endDateData));
  }, [dispatch, startDate, endDate]);

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getDashboardUserChart(startDateData, endDateData, "Short"));
  }, [dispatch, startDate, endDate]);
  useEffect(() => {
    dispatch(getDashboardUserChart(startDateData, endDateData, "Video"));
  }, [dispatch, startDate, endDate]);
  useEffect(() => {
    dispatch(getDashboardUserChart(startDateData, endDateData, "User"));
  }, [dispatch, startDate, endDate]);

  useEffect(() => {
    dispatch(getChartAnalyticOfActiveUser(startDateData, endDateData));
  }, [dispatch, startDate, endDate]);

  useEffect(() => {
    dispatch(getAdminEarningChart(startDateData, endDateData));
  }, [dispatch, startDate, endDate]);

  useEffect(() => {
    dispatch(getRecentMonetizationRequests());
    dispatch(getRecentWithdrawRequests());
  }, [dispatch]);

  const handlePeriodSelect = (period) => {
    setAnalyticsPeriod(period.key);
    const end = dayjs();
    let start = end;
    if (period.days != null) start = end.subtract(period.days, "day");
    if (period.months != null) start = end.subtract(period.months, "month");
    if (period.years != null) start = end.subtract(period.years, "year");
    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
  };

  const handleAnalyticsDateApply = (_event, picker) => {
    const start = dayjs(picker.startDate).format("YYYY-MM-DD");
    const end = dayjs(picker.endDate).format("YYYY-MM-DD");
    if (start === "1970-01-01") {
      setStartDate("All");
      setEndDate("All");
      setAnalyticsPeriod(null);
      return;
    }
    setStartDate(start);
    setEndDate(end);
    setAnalyticsPeriod(null);
  };

  const chartMaps = useMemo(() => {
    const toMap = (arr) => {
      const map = {};
      arr?.forEach((item) => {
        if (item?._id) map[item._id] = item?.count || 0;
      });
      return map;
    };
    const userMap = toMap(chartAnalyticOfUsers);
    const videoMap = toMap(chartAnalyticOfVideos);
    const shortMap = toMap(chartAnalyticOfShorts);
    const label = [
      ...new Set([
        ...Object.keys(userMap),
        ...Object.keys(videoMap),
        ...Object.keys(shortMap),
      ]),
    ].sort((a, b) => new Date(a) - new Date(b));

    return {
      label,
      dataUser: label.map((d) => userMap[d] || 0),
      dataVideo: label.map((d) => videoMap[d] || 0),
      dataShort: label.map((d) => shortMap[d] || 0),
    };
  }, [chartAnalyticOfUsers, chartAnalyticOfVideos, chartAnalyticOfShorts]);

  const { label, dataUser, dataVideo, dataShort } = chartMaps;

  var webSize =
    typeof window !== "undefined" && typeof $ !== "undefined"
      ? $(window).width()
      : window.innerWidth || 1200;
  const resHeight =
    webSize >= 992 ? 360 : webSize < 992 && webSize > 576 ? 320 : 280;
  const earningChartHeight =
    webSize >= 992 ? 500 : webSize < 992 && webSize > 576 ? 400 : 300;

  const totalSeries = [
    { name: "Total User", data: dataUser },
    { name: "Total Video", data: dataVideo },
    { name: "Total Short", data: dataShort },
  ];

  const optionsTotal = {
    chart: {
      type: "area",
      stacked: false,
      zoom: { enabled: false },
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif",
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: [3, 2.5, 2.5],
    },
    markers: {
      size: [5, 4, 4],
      strokeWidth: 2,
      strokeColors: "#fff",
      hover: { sizeOffset: 2 },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 90, 100],
      },
    },
    grid: {
      borderColor: "#f0f1f5",
      strokeDashArray: 4,
      padding: { left: 8, right: 8 },
    },
    yaxis: {
      show: true,
      labels: {
        style: { colors: "#9ca3af", fontSize: "11px" },
        formatter: (val) => Math.round(val),
      },
    },
    xaxis: {
      categories: label,
      tickAmount: Math.min(label.length, 8),
      labels: {
        style: { colors: "#9ca3af", fontSize: "11px" },
        formatter: formatAxisDate,
        rotate: 0,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        formatter: (val, opts) => {
          const raw = label[opts?.dataPointIndex] || val;
          return dayjs(raw).isValid()
            ? dayjs(raw).format("MMM DD, YYYY")
            : raw;
        },
      },
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "13px",
      fontWeight: 500,
      markers: { width: 10, height: 10, radius: 12 },
      itemMargin: { horizontal: 14, vertical: 0 },
    },
    colors: ["#ff5e75", "#6b7280", "#e11d48"],
  };

  const apiTotalUsers = Number(chartAnalyticOfActiveUser?.totalUsers) || 0;
  const activeUsersCount = Number(chartAnalyticOfActiveUser?.activeUsers) || 0;
  const blockedUsersCount = Number(chartAnalyticOfActiveUser?.blockedUsers) || 0;
  // Prefer active+blocked so All Time / zero-block cases stay correct;
  // Apex donut center labels are unreliable, so we render a custom overlay.
  const activityTotalUsers =
    activeUsersCount + blockedUsersCount ||
    apiTotalUsers ||
    Number(dashboardCount?.totalUsers) ||
    0;
  const activePercentageofUser = activityTotalUsers
    ? (activeUsersCount / activityTotalUsers) * 100
    : 0;
  const blockPercentageofUser = activityTotalUsers
    ? (blockedUsersCount / activityTotalUsers) * 100
    : 0;
  const seriesDonut = [
    activeUsersCount || 0,
    // Keep a tiny slice when all blocked are 0 so the ring still renders,
    // but center/legend always use real counts.
    blockedUsersCount > 0 ? blockedUsersCount : 0,
  ];

  const optionsDonut = {
    chart: {
      type: "donut",
      fontFamily: "Poppins, sans-serif",
      toolbar: { show: false },
    },
    labels: ["Active User", "Block User"],
    colors: ["#ff5e75", "#4b5563"],
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    legend: { show: false },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          const idx = opts?.seriesIndex;
          if (idx === 0) return `${activeUsersCount}`;
          return `${blockedUsersCount}`;
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: false,
          },
        },
      },
    },
    states: {
      hover: { filter: { type: "none" } },
      active: { filter: { type: "none" } },
    },
  };

  const userTrend = calcSeriesTrend(dataUser);
  const engagementTrend = userTrend;

  const sortedEarningChart = [...(adminEarningChart || [])].sort(
    (a, b) => new Date(a?._id) - new Date(b?._id)
  );
  const earningLabels = sortedEarningChart.map((item) => item?._id);
  const earningSeries = [
    {
      name: "Coin Plan Earning",
      data: sortedEarningChart.map((item) => item?.coinPlanEarning || 0),
    },
    {
      name: "Premium Plan Earning",
      data: sortedEarningChart.map((item) => item?.premiumPlanEarning || 0),
    },
    {
      name: "Total Earning",
      data: sortedEarningChart.map((item) => item?.totalEarning || 0),
    },
  ];

  const hasAnalyticsData = label?.length > 0;
  const hasEarningData = sortedEarningChart?.length > 0;
  const hasUserActivityData =
    activityTotalUsers > 0 ||
    activeUsersCount > 0 ||
    blockedUsersCount > 0;

  const isAllSelected =
    startDate === "All" ||
    startDate === "ALL" ||
    startDate === "1970-01-01";

  const analyticsDateLabel =
    !isAllSelected &&
    startDate &&
    endDate &&
    dayjs(startDate).isValid() &&
    dayjs(endDate).isValid()
      ? `${dayjs(startDate).format("DD/MM/YYYY")} - ${dayjs(endDate).format(
          "DD/MM/YYYY"
        )}`
      : "Select Date Range";

  const optionsEarning = {
    chart: {
      type: "area",
      stacked: false,
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        inverseColors: false,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [20, 100, 100, 100],
      },
    },
    yaxis: {
      show: true,
      labels: {
        formatter: function (val) {
          return Math.round(val);
        },
      },
    },
    xaxis: {
      categories: earningLabels,
      rotate: 0,
      rotateAlways: true,
      minHeight: 50,
      maxHeight: 100,
      labels: {
        offsetX: -4,
        fontSize: 10,
      },
    },
    tooltip: {
      shared: true,
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      offsetX: -10,
    },
    colors: ["#786D81", "#FD4D66", "#e91e63"],
  };

  const getRequestStatus = (status) => {
    if (status === 1) return { label: "Pending", className: "text-warning" };
    if (status === 2) return { label: "Accepted", className: "text-success" };
    if (status === 3) return { label: "Declined", className: "text-danger" };
    return { label: "-", className: "" };
  };

  const monetizationTableMap = [
    {
      Header: "NO",
      body: "no",
      Cell: ({ index }) => <span className="text-nowrap">{index + 1}</span>,
    },
    {
      Header: "USERNAME",
      body: "userName",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <LazyImage imageSrc={row?.user?.image} width="40px" height="40px" />
          <div className="ms-3 text-start">
            <div className="fw-semibold text-capitalize">
              {row?.user?.fullName || "-"}
            </div>
            <div className="text-muted small text-capitalize">
              {row?.user?.nickName || "-"}
            </div>
            <UniqueIdCopy value={row?.user?.uniqueId} placeholder="-" />
          </div>
        </div>
      ),
    },
    {
      Header: "CHANNEL",
      body: "channelName",
      Cell: ({ row }) => (
        <span className="text-capitalize">{row?.channelName || "-"}</span>
      ),
    },
    {
      Header: "MIN SUBSCRIBERS",
      body: "minSubScriber",
      Cell: ({ row }) => <span>{row?.minSubScriber ?? 0}</span>,
    },
    {
      Header: "MIN WATCH TIME (HOURS)",
      body: "minWatchTime",
      Cell: ({ row }) => <span>{row?.minWatchTime ?? 0}</span>,
    },
    {
      Header: "TOTAL SUBSCRIBERS",
      body: "totalSubScribers",
      Cell: ({ row }) => <span>{row?.totalSubScribers ?? 0}</span>,
    },
    {
      Header: "TOTAL WATCH TIME (MINUTES)",
      body: "totalWatchTime",
      Cell: ({ row }) => <span>{row?.totalWatchTime ?? 0}</span>,
    },
    {
      Header: "TOTAL WATCH TIME (HOURS)",
      body: "totalWatchTimeInHours",
      Cell: ({ row }) => <span>{row?.totalWatchTimeInHours ?? 0}</span>,
    },
    {
      Header: "DATE",
      body: "createdAt",
      Cell: ({ row }) => (
        <span className="text-nowrap">
          {row?.createdAt
            ? dayjs(row.createdAt).format("MM/DD/YYYY hh:mm A")
            : "-"}
        </span>
      ),
    },
    {
      Header: "STATUS",
      body: "status",
      Cell: ({ row }) => {
        const statusInfo = getRequestStatus(row?.status);
        return (
          <div className={`action-button ${statusInfo.className}`}>
            {statusInfo.label}
          </div>
        );
      },
    },
  ];

  const withdrawTableMap = [
    {
      Header: "NO",
      body: "no",
      Cell: ({ index }) => <span className="text-nowrap">{index + 1}</span>,
    },
    {
      Header: "USERNAME",
      body: "userName",
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <LazyImage imageSrc={row?.user?.image} width="40px" height="40px" />
          <div className="ms-3 text-start">
            <div className="fw-semibold text-capitalize">
              {row?.user?.fullName || "-"}
            </div>
            <div className="text-muted small text-capitalize">
              {row?.user?.nickName || "-"}
            </div>
            <UniqueIdCopy value={row?.user?.uniqueId} placeholder="-" />
          </div>
        </div>
      ),
    },
    {
      Header: "CHANNEL",
      body: "channelName",
      Cell: ({ row }) => (
        <span className="text-capitalize">{row?.channelName || "-"}</span>
      ),
    },
    {
      Header: "TRANSACTION ID",
      body: "uniqueId",
      Cell: ({ row }) => (
        <UniqueIdCopy value={row?.uniqueId} placeholder="-" />
      ),
    },
    {
      Header: "AMOUNT",
      body: "requestAmount",
      Cell: ({ row }) => <span>{row?.requestAmount ?? 0}</span>,
    },
    {
      Header: "GATEWAY",
      body: "paymentGateway",
      Cell: ({ row }) => <span>{row?.paymentGateway || "-"}</span>,
    },
    {
      Header: "PAYMENT DETAILS",
      body: "paymentDetails",
      Cell: ({ row }) => (
        <div className="text-start">
          {row?.paymentDetails?.length > 0 ? (
            row.paymentDetails.map((item, index) => (
              <div key={index} className="text-nowrap small">
                {item}
              </div>
            ))
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
    {
      Header: "DATE",
      body: "createdAt",
      Cell: ({ row }) => (
        <span className="text-nowrap">
          {row?.createdAt
            ? dayjs(row.createdAt).format("MM/DD/YYYY hh:mm A")
            : "-"}
        </span>
      ),
    },
    {
      Header: "STATUS",
      body: "status",
      Cell: ({ row }) => {
        const statusInfo = getRequestStatus(row?.status);
        return (
          <div className={`action-button ${statusInfo.className}`}>
            {statusInfo.label}
          </div>
        );
      },
    },
  ];

  const CustomeCard = ({ link, title, count, Icon }) => {
    return (
      <div
        className={`col-xl-3 col-sm-6 col-12 ${link ? "cursor-pointer" : ""}`}
        onClick={() => !isLoading && link && navigate(link)}
      >
        <div className="card">
          {isLoading ? (
            <StatsCardSkeleton />
          ) : (
            <div className={`card-content ${link ? "cursor-pointer" : ""}`}>
              <div className="card-body p-4">
                <div className="align-content-center d-flex justify-content-between media">
                  <div className="media-body text-left">
                    <h3 className="warning">{count ?? "0"}</h3>
                    <span className="fw-medium">{title}</span>
                  </div>
                  <div className="align-self-center">
                    {<Icon className={"dashboard-card-icon"} />}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="dashboard " style={{marginTop: "0px" }}>
        <div className="dashboardHeader primeHeader !mb-0 !p-0">
          <h4 className="heading-dashboard fw-semibold d-block">
            Welcome Admin !
          </h4>
          <NewTitle
            dayAnalyticsShow={true}
            titleShow={true}
            setEndDate={(value) => {
              setAnalyticsPeriod(null);
              setEndDate(value);
            }}
            setStartDate={(value) => {
              setAnalyticsPeriod(null);
              setStartDate(value);
            }}
            startDate={startDate}
            endDate={endDate}
            name={`Dashboard`}
          />
        </div>
        <div className="dashBoardMain px-2 mt-4">
          <div className="row dashboard-count-box">
            <CustomeCard
              link={"/admin/userTable"}
              title={"Total Users"}
              count={dashboardCount?.totalUsers}
              Icon={IconUser}
            />
            <CustomeCard
              link={"/admin/channel"}
              title={"Total Channels"}
              count={dashboardCount?.totalChannels}
              Icon={IconBrandYoutube}
            />
            <CustomeCard
              link={"/admin/videos"}
              title={"Total Videos"}
              count={dashboardCount?.totalVideos}
              Icon={IconVideo}
            />
            <CustomeCard
              link={"/admin/shorts"}
              title={"Total Shorts"}
              count={dashboardCount?.totalShorts}
              Icon={IconMovie}
            />
            <CustomeCard
              link={"/admin/withdrawRequest"}
              title={"Pending Withdraws"}
              count={dashboardCount?.pendingWithdraws}
              Icon={IconCash}
            />
            <CustomeCard
              link={"/admin/allmonetizationRequest"}
              title={"Pending Monetization"}
              count={dashboardCount?.pendingMonetization}
              Icon={IconCashBanknote}
            />
            <CustomeCard
              link={"/admin/allreport"}
              title={"Total Reports"}
              count={dashboardCount?.totalReports}
              Icon={IconReport}
            />
            <CustomeCard
              link={"/admin/adminEarnings"}
              title={"Total Earnings"}
              count={dashboardCount?.totalEarnings}
              Icon={IconWallet}
            />
            <CustomeCard
              link={"/admin/adminEarnings"}
              title={"Coin Plan Earnings"}
              count={dashboardCount?.coinPlanEarnings}
              Icon={IconCoin}
            />
            <CustomeCard
              link={"/admin/adminEarnings"}
              title={"Premium Plan Earnings"}
              count={dashboardCount?.premiumPlanEarnings}
              Icon={IconPremiumRights}
            />
            <CustomeCard
              title={"Total Advertisements"}
              count={dashboardCount?.totalAdvertisements}
              Icon={IconAd}
            />
            <CustomeCard
              link={"/admin/premiumPlanTable"}
              title={"Total Subscriptions"}
              count={dashboardCount?.totalSubscriptions}
              Icon={IconUsers}
            />
          </div>
          <div className="dashboard-analytics da-panel">
            <div className="da-panel__header">
              <div className="da-panel__title-wrap">
                <h6 className="da-panel__title">Data Analytics</h6>
                <p className="da-panel__subtitle mb-0">
                  Overview of users, videos and shorts performance
                </p>
              </div>
              <div className="da-panel__controls">
                
                <div className="da-date-picker">
                  <DateRangePicker
                    initialSettings={{
                      startDate: undefined,
                      endDate: undefined,
                      alwaysShowCalendars: true,
                      showDropdowns: false,
                      autoUpdateInput: false,
                      opens: "left",
                      locale: datePickerLocale,
                      ranges: getDateRanges(),
                      maxDate: new Date(),
                    }}
                    onShow={attachDateRangeCustomActiveFix}
                    onApply={handleAnalyticsDateApply}
                  >
                    <button
                      type="button"
                      className="date-range-trigger da-date-trigger"
                      aria-label="Select analytics date range"
                    >
                      <IconCalendarEvent
                        className="date-range-trigger__icon"
                        size={18}
                        stroke={2}
                      />
                      <span className="date-range-trigger__label">
                        {analyticsDateLabel}
                      </span>
                      <IconChevronDown
                        className="date-range-trigger__chevron"
                        size={18}
                        stroke={2}
                      />
                    </button>
                  </DateRangePicker>
                </div>
              </div>
            </div>

            <div className="da-metric-row">
              {isLoading ? (
                <>
                  <AnalyticsMetricSkeleton />
                  <AnalyticsMetricSkeleton />
                  <AnalyticsMetricSkeleton />
                </>
              ) : (
                <>
                  <AnalyticsMetricCard
                    title="Total Users"
                    count={dashboardCount?.totalUsers}
                    Icon={IconUser}
                    accent="accent-users"
                  />
                  <AnalyticsMetricCard
                    title="Total Videos"
                    count={dashboardCount?.totalVideos}
                    Icon={IconPlayerPlay}
                    accent="accent-videos"
                  />
                  <AnalyticsMetricCard
                    title="Total Shorts"
                    count={dashboardCount?.totalShorts}
                    Icon={IconMovie}
                    accent="accent-shorts"
                  />
                </>
              )}
            </div>

            <div className="row da-body g-3">
              <div className="col-lg-8 col-xl-9">
                <div className="da-chart-card">
                  {isLoading ? (
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={resHeight}
                      style={{ borderRadius: 15 }}
                    />
                  ) : hasAnalyticsData ? (
                    <ReactApexChart
                      options={optionsTotal}
                      series={totalSeries}
                      type="area"
                      height={resHeight}
                    />
                  ) : (
                    <ChartEmptyState title="Analytics Data" height={resHeight} />
                  )}
                </div>
              </div>
              <div className="col-lg-4 col-xl-3">
                <div className="da-activity-card">
                  <div className="da-activity-card__header">
                    <h6 className="mb-0">Total User Activity</h6>
                    <IconInfoCircle size={16} stroke={1.8} color="#9ca3af" />
                  </div>
                  <div className="da-activity-card__chart">
                    {isLoading ? (
                      <Skeleton
                        variant="circular"
                        width={200}
                        height={200}
                        style={{ margin: "0 auto" }}
                      />
                    ) : hasUserActivityData ? (
                      <div className="da-donut-wrap">
                        <ReactApexChart
                          key={`user-activity-${activityTotalUsers}-${activeUsersCount}-${blockedUsersCount}`}
                          options={optionsDonut}
                          series={
                            activeUsersCount + blockedUsersCount > 0
                              ? seriesDonut
                              : [1]
                          }
                          type="donut"
                          height={240}
                        />
                        <div className="da-donut-center">
                          <strong>{activityTotalUsers}</strong>
                          <span>Total Users</span>
                        </div>
                      </div>
                    ) : (
                      <ChartEmptyState
                        title="User Activity"
                        height={240}
                        message="User activity will appear here once available."
                      />
                    )}
                  </div>
                  {hasUserActivityData && (
                    <>
                      <div className="da-activity-legend">
                        <div className="da-activity-legend__item">
                          <span className="da-dot da-dot--active" />
                          <div className="da-activity-legend__meta">
                            <span className="da-activity-legend__label">
                              Active User
                            </span>
                            <span className="da-activity-legend__value">
                              {activeUsersCount} (
                              {activePercentageofUser.toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                        <div className="da-activity-legend__item">
                          <span className="da-dot da-dot--blocked" />
                          <div className="da-activity-legend__meta">
                            <span className="da-activity-legend__label">
                              Block User
                            </span>
                            <span className="da-activity-legend__value">
                              {blockedUsersCount} (
                              {blockPercentageofUser.toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="da-insight-box">
                        <div className="da-insight-box__icon">
                          {engagementTrend?.up ? (
                            <IconTrendingUp size={16} stroke={2} />
                          ) : (
                            <IconTrendingDown size={16} stroke={2} />
                          )}
                        </div>
                        <p className="mb-0">
                          {engagementTrend ? (
                            <>
                              User engagement is{" "}
                              {engagementTrend.up ? "up" : "down"} by{" "}
                              <strong>{engagementTrend.pct}%</strong> compared
                              to last period.
                            </>
                          ) : (
                            <>
                              User engagement trends will appear once enough
                              period data is available.
                            </>
                          )}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-analytics mt-4">
            <h6 className="">Admin Earning Analytics</h6>
            <div className="row dashboard-chart">
              <div
                className="col-12 mt-lg-0 mt-4 dashboard-chart-box"
                style={{ position: "relative" }}
              >
                {isLoading ? (
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={earningChartHeight}
                    style={{ borderRadius: "15px" }}
                  />
                ) : hasEarningData ? (
                  <ReactApexChart
                    options={optionsEarning}
                    series={earningSeries}
                    type="area"
                    height={earningChartHeight}
                  />
                ) : (
                  <ChartEmptyState
                    title="Earning Data"
                    height={earningChartHeight}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-12 mb-4">
              <div className="user-table real-user mb-3">
                <div className="user-table-top">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5
                      style={{
                        fontWeight: "500",
                        fontSize: "20px",
                        marginTop: "5px",
                        marginBottom: "4px",
                      }}
                    >
                      Recent Monetization Requests
                    </h5>
                    <span
                      className="cursor-pointer text-secondary fw-medium"
                      onClick={() => navigate("/admin/allmonetizationRequest")}
                    >
                      View All
                    </span>
                  </div>
                </div>
                <Table
                  data={recentMonetizationRequests || []}
                  mapData={monetizationTableMap}
                  PerPage={recentMonetizationRequests?.length || 10}
                  Page={1}
                  type={"server"}
                  loading={isLoading}
                />
              </div>
            </div>
            <div className="col-12 mb-4">
              <div className="user-table real-user mb-3">
                <div className="user-table-top">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5
                      style={{
                        fontWeight: "500",
                        fontSize: "20px",
                        marginTop: "5px",
                        marginBottom: "4px",
                      }}
                    >
                      Recent Withdraw Requests
                    </h5>
                    <span
                      className="cursor-pointer text-secondary fw-medium"
                      onClick={() => navigate("/admin/withdrawRequest")}
                    >
                      View All
                    </span>
                  </div>
                </div>
                <Table
                  data={recentWithdrawRequests || []}
                  mapData={withdrawTableMap}
                  PerPage={recentWithdrawRequests?.length || 10}
                  Page={1}
                  type={"server"}
                  loading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(null, {
  getProfile,
  getDashboardCount,
  getDashboardUserChart,
  getChartAnalyticOfActiveUser,
  getAdminEarningChart,
  getRecentMonetizationRequests,
  getRecentWithdrawRequests,
})(Dashboard);
